// frontend/src/pages/admin/Stats.tsx
import React, { useEffect, useState } from 'react';
import { Users, TrendingUp, Award, BarChart3, Calendar } from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import api from '../../services/api';

interface StatsData {
  overview: {
    total_users: number;
    active_users: number;
    total_products: number;
    active_promotions: number;
    completion_rate: number;
    average_score: number;
  };
  users_by_role: { agent: number; formateur: number; admin: number };
  monthly_registrations: Array<{ month: string; count: number }>;
  promotion_performance: Array<{
    name: string;
    users: number;
    completion: number;
    quiz_success: number;
  }>;
}

export default function AdminStats() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/stats/detailed', { params: { range: timeRange } });
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError('Impossible de charger les statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/stats/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'stats.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Erreur lors de l'export des statistiques:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (error) {
    return <div className="flex items-center justify-center min-h-[60vh] text-red-600">{error}</div>;
  }
  if (!stats) {
    return <div className="flex items-center justify-center min-h-[60vh]">Aucune donnée disponible.</div>;
  }

  const COLORS = ['#004E89', '#00A6FB', '#FFA500', '#4CAF50'];

  const userRoleData = [
    { name: 'Agents', value: stats.users_by_role.agent },
    { name: 'Formateurs', value: stats.users_by_role.formateur },
    { name: 'Admins', value: stats.users_by_role.admin }
  ];

  // === Helpers lisibilité ===
  const truncate = (s: string, max = 16) => (s.length > max ? s.slice(0, max - 1) + '…' : s);

  // Largeurs minimales pour éviter chevauchements sur petits écrans
  const monthlyMinWidth = Math.max(520, stats.monthly_registrations.length * 72);
  const perfMinWidth = Math.max(840, stats.promotion_performance.length * 180);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statistiques globales</h1>
          <p className="mt-1 text-gray-600">Vue d'ensemble de la plateforme</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="w-full sm:w-auto border-gray-300 rounded-md focus:ring-primary focus:border-primary"
        >
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
          <option value="quarter">Ce trimestre</option>
          <option value="year">Cette année</option>
        </select>
      </div>

      {/* Cartes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Utilisateurs totaux</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overview.total_users}</p>
              <p className="text-xs text-green-600 mt-1">{stats.overview.active_users} actifs</p>
            </div>
            <Users className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Taux de complétion</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overview.completion_rate}%</p>
              <p className="text-xs text-gray-500 mt-1">Moyenne globale</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Score moyen</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overview.average_score}%</p>
              <p className="text-xs text-gray-500 mt-1">Quiz et simulations</p>
            </div>
            <Award className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Promotions actives</p>
              <p className="text-2xl font-bold text-gray-900">{stats.overview.active_promotions}</p>
              <p className="text-xs text-gray-500 mt-1">Sur {stats.overview.total_products} produits</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition des utilisateurs */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold mb-4">Répartition des utilisateurs</h2>
          <div className="h-56 sm:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={userRoleData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {userRoleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Évolution des inscriptions */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold mb-4">Évolution des inscriptions</h2>
          <div className="overflow-x-auto">
            <div className="h-56 sm:h-64" style={{ minWidth: monthlyMinWidth }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.monthly_registrations}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#004E89" fill="#004E89" fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Performance des promotions — lisible */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-4">Performance des promotions</h2>
        <div className="overflow-x-auto">
          <div className="h-64 sm:h-80" style={{ minWidth: perfMinWidth }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stats.promotion_performance}
                margin={{ top: 36, right: 24, left: 8, bottom: 56 }}
                barCategoryGap={28}
              >
                <CartesianGrid strokeDasharray="3 3" />

                {/* Libellés X tronqués (tooltip montre le nom complet) */}
                <XAxis
                  dataKey="name"
                  interval={0}
                  height={40}
                  tickMargin={10}
                  tickFormatter={(v: string) => truncate(v, 16)}
                />

                {/* Deux axes Y pour séparer volumes et pourcentages */}
                <YAxis yAxisId="left" orientation="left" allowDecimals={false} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />

                <Tooltip
                  formatter={(value: any, name: string) =>
                    name.includes('%') ? [`${value}%`, name] : [value, name]
                  }
                  labelFormatter={(label) => label} // nom complet au survol
                />

                {/* Légende au-dessus, séparée du graphe */}
                <Legend verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: 8 }} />

                <Bar yAxisId="left" dataKey="users" fill="#004E89" name="Agents" />
                <Bar yAxisId="right" dataKey="completion" fill="#00A6FB" name="Taux complétion %" />
                <Bar yAxisId="right" dataKey="quiz_success" fill="#4CAF50" name="Taux réussite quiz %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Action rapide */}
      <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
        <button
          onClick={handleExport}
          className="bg-white rounded-lg shadow p-4 md:p-6 hover:shadow-lg transition-shadow text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Exporter rapport complet</h3>
              <p className="text-sm text-gray-600 mt-1">PDF avec tous les graphiques</p>
            </div>
            <BarChart3 className="h-8 w-8 text-gray-400" />
          </div>
        </button>
      </div>
    </div>
  );
}
